import React from 'react';
import { formatCurrency, getCategorySpent, getProgressColor, cn } from '../../lib/utils';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import Button from '../ui/Button';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableTableRow = ({ category, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const BudgetTable = ({
  categoryGroups,
  categories,
  allocations,
  transactions,
  expandedGroups,
  onToggleGroup,
  onUpdateBudget,
  onAddTransaction,
  editingBudget,
  setEditingBudget,
  onDragEnd,
  sensors,
  onEditCategory,
  onEditGroup,
  onViewTransactions,
  onAddCategory,
}) => {
  return (
    <div className="w-full bg-background border border-border overflow-hidden">
      <div className="grid grid-cols-[minmax(200px,1fr)_120px_120px_120px_60px] gap-4 px-6 py-3 bg-background border-b border-border text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        <div>Category</div>
        <div className="text-right">Budgeted</div>
        <div className="text-right">Spent</div>
        <div className="text-right">Remaining</div>
        <div></div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="divide-y divide-border">
        {categoryGroups.map((group) => {
          const groupCategories = categories
            .filter(c => c.group_id === group.id)
            .sort((a, b) => a.sort_order - b.sort_order);
          const isExpanded = expandedGroups.has(group.id);

          const groupBudgeted = groupCategories.reduce((sum, cat) => sum + (allocations.get(cat.id) || 0), 0);
          const groupSpent = groupCategories.reduce((sum, cat) => sum + getCategorySpent(transactions, cat.id), 0);
          const groupProgress = groupBudgeted > 0 ? (groupSpent / groupBudgeted) * 100 : 0;
          const progressColor = getProgressColor(groupProgress);

          return (
            <div key={group.id} className="relative">
              {/* Group Header */}
              <div
                onClick={() => onToggleGroup(group.id)}
                className={cn(
                  'w-full flex items-center justify-between px-6 py-4 transition-colors relative bl-indicator cursor-pointer',
                  isExpanded ? 'bl-on hover:bg-accent' : 'hover:bg-accent'
                )}
              >
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <button
                    onClick={(e) => { e.stopPropagation(); onEditGroup && onEditGroup(group); }}
                    className="font-semibold text-foreground text-left hover:opacity-70 transition-opacity"
                  >
                    {group.name}
                  </button>
                </div>
                <div className="text-right font-mono tabular-nums text-muted-foreground text-sm">
                  {formatCurrency(groupSpent)} of {formatCurrency(groupBudgeted)}
                </div>
              </div>

              {/* Progress Glow Indicator */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-background/30 overflow-hidden">
                <div
                  className="h-full transition-all duration-500 ease-out"
                  style={{
                    width: `${Math.min(groupProgress, 100)}%`,
                    backgroundColor: progressColor,
                    boxShadow: `0 0 8px ${progressColor}`
                  }}
                />
              </div>

              {/* Category Rows */}
              {isExpanded && (
                  <SortableContext items={groupCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    {groupCategories.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
                        <p className="text-sm">No categories for this group.</p>
                        {onAddCategory && (
                          <button
                            onClick={() => onAddCategory(group)}
                            className="text-sm flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:opacity-70 transition-opacity"
                          >
                            <Plus className="h-3 w-3" /> Add category
                          </button>
                        )}
                      </div>
                    )}
                    {groupCategories.map((category) => {
                      const budgeted = allocations.get(category.id) || 0;
                      const spent = getCategorySpent(transactions, category.id);
                      const remaining = budgeted - spent;
                      const isEditing = editingBudget === category.id;

                      const commitEdit = (e) => {
                        onUpdateBudget(category.id, parseFloat(e.target.value) || 0);
                        setEditingBudget(null);
                      };

                      return (
                        <SortableTableRow key={category.id} category={category}>
                          <div
                            className={cn(
                              'grid grid-cols-[minmax(200px,1fr)_120px_120px_120px_60px] gap-4 px-6 py-3 transition-colors items-center text-sm bl-indicator cursor-pointer',
                              isEditing ? 'bl-on' : 'hover:bg-accent'
                            )}
                            onClick={() => onViewTransactions && onViewTransactions(category)}
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); onEditCategory && onEditCategory(category); }}
                              className="pl-8 text-foreground/90 text-left hover:opacity-70 transition-opacity w-full"
                            >
                              {category.name}
                            </button>

                            <div className="text-right">
                              {isEditing ? (
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  defaultValue={budgeted}
                                  onBlur={commitEdit}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitEdit(e);
                                    if (e.key === 'Escape') setEditingBudget(null);
                                  }}
                                  autoFocus
                                  className="w-full px-2 py-1 text-right font-mono tabular-nums bg-input border border-border focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                                />
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingBudget(category.id); }}
                                  className="w-full px-2 py-1 text-right font-mono tabular-nums text-foreground hover:opacity-70 transition-opacity"
                                >
                                  {formatCurrency(budgeted)}
                                </button>
                              )}
                            </div>

                            <div className="text-right font-mono tabular-nums text-foreground/90">
                              {formatCurrency(spent)}
                            </div>

                            {/* Remaining */}
                            <div className={cn(
                              "text-right font-mono tabular-nums font-semibold",
                              remaining >= 0 ? "text-success" : "text-destructive"
                            )}>
                              {formatCurrency(remaining)}
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); onAddTransaction(category); }}
                                className="h-7 w-7 hover:bg-accent"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </SortableTableRow>
                      );
                    })}
                  </SortableContext>
              )}
            </div>
          );
        })}
      </div>
      </DndContext>
    </div>
  );
};

export default BudgetTable;
